import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

const DATA = {
  AU: {
    flag: "🇦🇺", name: "Australia", variant: "Australian English",
    tagline: "The English you need for Australian life and work.",
    intro: "From talking to your manager to booking a GP, we help you practise the everyday English that matters most in Australia.",
    categories: ["Work", "Everyday Life", "Healthcare", "Renting", "Banking", "Transport", "Shopping", "School", "Job Interviews", "Small Talk"],
    examples: [
      "Introducing yourself to colleagues", "Talking to your manager about a delay",
      "Calling a GP and making an appointment", "Rental inspection with a real estate agent",
      "Opening a bank account", "Talking to your child's teacher",
    ],
  },
  CA: {
    flag: "🇨🇦", name: "Canada", variant: "Canadian English",
    tagline: "The English you need for Canadian life and work.",
    intro: "Registering with a family doctor, understanding your hydro bill, or handling a Canadian job interview — practise what you actually face.",
    categories: ["Workplace", "Healthcare", "Renting", "Banking", "Job interviews", "School", "Transport", "Everyday conversation"],
    examples: [
      "Registering with a family doctor", "Renting an apartment in Toronto",
      "Behavioural interview questions", "Ordering at a café (small talk)",
    ],
  },
  UK: {
    flag: "🇬🇧", name: "United Kingdom", variant: "British English",
    tagline: "The English you need for life and work in the UK.",
    intro: "Registering with an NHS GP, tenancy agreements, and understanding polite indirectness at work — practise what really matters.",
    categories: ["Workplace", "Healthcare", "Renting", "Banking", "Job interviews", "School", "Transport", "Everyday conversation"],
    examples: [
      "Registering with an NHS GP", "Speaking to a letting agent",
      "Small talk about the weather", "UK-style behavioural interview",
    ],
  },
};

export default function CountryPage() {
  const { code } = useParams();
  const c = DATA[code?.toUpperCase()];
  if (!c) return <Navigate to="/" replace />;
  return (
    <div>
      <section className="rle-gradient-bg py-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-6xl">{c.flag}</div>
          <span className="rle-eyebrow mt-4 block">{c.variant}</span>
          <h1 className="mt-2 font-display font-extrabold text-4xl sm:text-5xl text-slate-900">{c.tagline}</h1>
          <p className="mt-4 text-lg text-slate-600 max-w-3xl">{c.intro}</p>
          <div className="mt-8"><Link to="/register"><Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 h-12 px-6" data-testid={`country-${code.toLowerCase()}-cta-btn`}>Start Learning Free <ArrowRight size={16} className="ml-1"/></Button></Link></div>
        </div>
      </section>
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display font-bold text-2xl text-slate-900">Categories we cover</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {c.categories.map((cat) => <span key={cat} className="rle-chip">{cat}</span>)}
            </div>
          </div>
          <div>
            <h2 className="font-display font-bold text-2xl text-slate-900">Example scenarios</h2>
            <ul className="mt-4 space-y-2">
              {c.examples.map((e) => <li key={e} className="rle-card p-4 text-slate-800 text-sm">{e}</li>)}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
